import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    FiUsers, FiFilePlus, FiClipboard, FiCheckCircle, FiXCircle, 
    FiAlertTriangle, FiUpload, FiTrash2, FiSearch, FiArrowRight, 
    FiArrowLeft, FiPieChart, FiDollarSign, FiCalendar, FiActivity,
    FiClock, FiPrinter, FiMoreVertical, FiDownload
} from 'react-icons/fi';
import { FaCalculator, FaMoneyBillWave, FaUserTie } from 'react-icons/fa'; 
import { toast } from 'react-toastify';
import './RescisoesDemissoes.css';
import api from '../../services/api'; 

// --- Utilitários ---
const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
const formatDate = (date) => {
    if(!date) return '-';
    const d = new Date(date);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
};

// --- Motor de Cálculo (Simulação Front-end) ---
const calcularVerbasSimuladas = (dados, saldoFGTS = 0) => {
    const salario = parseFloat(dados.salario) || 0;
    const dataSaida = new Date(dados.data_saida);
    const diasTrabalhados = dataSaida.getDate();
    
    // Cálculos Base Simplificados
    const saldoSalario = (salario / 30) * diasTrabalhados;
    const decimoTerceiroProp = (salario / 12) * (dataSaida.getMonth() + 1);
    const feriasProp = (salario / 12) * (dataSaida.getMonth() + 1); 
    const tercoFerias = feriasProp / 3;

    let proventos = [
        { desc: `Saldo de Salário (${diasTrabalhados} dias)`, val: saldoSalario, tipo: 'provento' },
        { desc: '13º Salário Proporcional', val: decimoTerceiroProp, tipo: 'provento' },
        { desc: 'Férias Proporcionais', val: feriasProp, tipo: 'provento' },
        { desc: '1/3 Constitucional de Férias', val: tercoFerias, tipo: 'provento' }
    ];

    if (dados.tipo_rescisao === 'demissao_sem_justa_causa') {
        proventos.push({ desc: 'Aviso Prévio Indenizado (30 dias)', val: salario, tipo: 'provento' });
        proventos.push({ desc: 'Multa 40% do FGTS', val: saldoFGTS * 0.4, tipo: 'provento' });
    }

    const bruto = proventos.reduce((acc, i) => acc + i.val, 0);
    
    // Descontos Estimados
    const inss = bruto * 0.09; 
    const irrf = bruto > 2500 ? (bruto * 0.075) : 0;

    let descontos = [
        { desc: 'INSS (Estimado)', val: inss, tipo: 'desconto' },
        { desc: 'IRRF (Estimado)', val: irrf, tipo: 'desconto' }
    ];

    return [...proventos, ...descontos];
};

// =========================================================================================
// 💎 COMPONENTE PRINCIPAL
// =========================================================================================
const RescisoesDemissoes = () => {
    const [rescissoes, setRescissoes] = useState([]);
    const [funcionariosAtivos, setFuncionariosAtivos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('active'); // 'active' | 'history'
    
    // Estados de Modal
    const [viewState, setViewState] = useState('list'); // 'list', 'wizard', 'details'
    const [selectedItem, setSelectedItem] = useState(null);

    // KPIs
    const stats = useMemo(() => {
        const pending = rescissoes.filter(r => r.status !== 'concluida' && r.status !== 'cancelada');
        const done = rescissoes.filter(r => r.status === 'concluida');
        const totalValue = pending.reduce((acc, curr) => acc + (parseFloat(curr.salario) || 0), 0); 
        return { 
            pending: pending.length, 
            done: done.length, 
            estimated: totalValue 
        };
    }, [rescissoes]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [resRes, colabRes] = await Promise.all([
                api.get('/api/rh/rescissoes'),
                api.get('/api/rh/colaboradores')
            ]);
            setRescissoes(resRes.data);
            setFuncionariosAtivos(colabRes.data.filter(c => c.status === 'ativo'));
        } catch (err) {
            console.error(err);
            toast.error("Não foi possível sincronizar os dados.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleDetail = async (id) => {
        try {
            const res = await api.get(`/api/rh/rescissoes/${id}`);
            setSelectedItem(res.data);
            setViewState('details');
        } catch (e) { toast.error("Erro ao abrir detalhes."); }
    };

    const handleSoftDelete = async (id) => {
        if(!window.confirm("Confirmar exclusão do processo?")) return;
        try {
            await api.delete(`/api/rh/rescissoes/${id}`);
            toast.success("Registro removido.");
            fetchData();
        } catch (e) { toast.error("Erro ao excluir."); }
    };

    const displayList = rescissoes.filter(r => 
        activeTab === 'active' 
            ? (r.status !== 'concluida' && r.status !== 'cancelada') 
            : (r.status === 'concluida' || r.status === 'cancelada')
    );

    return (
        <div className="rescissoes-container fade-in-up">
            {viewState === 'list' && (
                <>
                    <header className="page-header">
                        <div className="header-content">
                            <h1><FiUsers /> Central de Desligamentos</h1>
                            <p>Gerencie rescisões, cálculos e conformidade trabalhista.</p>
                        </div>
                        <button className="btn-create" onClick={() => setViewState('wizard')}>
                            <FiFilePlus /> Iniciar Desligamento
                        </button>
                    </header>

                    <div className="stats-row">
                        <div className="stat-card">
                            <div className="icon-box blue"><FiActivity /></div>
                            <div>
                                <h3>{stats.pending}</h3>
                                <span>Processos Ativos</span>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="icon-box green"><FiCheckCircle /></div>
                            <div>
                                <h3>{stats.done}</h3>
                                <span>Concluídos</span>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="icon-box purple"><FiDollarSign /></div>
                            <div>
                                <h3>{formatCurrency(stats.estimated)}</h3>
                                <span>Vol. Salarial em Análise</span>
                            </div>
                        </div>
                    </div>

                    <div className="main-content-card">
                        <div className="tabs-header">
                            <button className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`} onClick={() => setActiveTab('active')}>
                                Em Andamento ({stats.pending})
                            </button>
                            <button className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
                                Histórico ({stats.done})
                            </button>
                        </div>

                        <div className="table-responsive">
                            <table className="table-premium">
                                <thead>
                                    <tr>
                                        <th>Colaborador</th>
                                        <th>Motivo</th>
                                        <th>Data Saída</th>
                                        <th>Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayList.length > 0 ? displayList.map(item => (
                                        <tr key={item.id} className="hover-row">
                                            <td>
                                                <div className="user-profile">
                                                    <div className="avatar">{item.nome_colaborador.charAt(0)}</div>
                                                    <div>
                                                        <strong>{item.nome_colaborador}</strong>
                                                        <span>ID #{item.id}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td><span className="badge-type">{item.tipo_rescisao?.replace(/_/g, ' ')}</span></td>
                                            <td><div className="date-tag"><FiCalendar /> {formatDate(item.data_saida)}</div></td>
                                            <td><StatusBadge status={item.status} /></td>
                                            <td className="text-right">
                                                <button className="btn-icon" onClick={() => handleDetail(item.id)} title="Detalhes"><FiArrowRight /></button>
                                                <button className="btn-icon danger" onClick={() => handleSoftDelete(item.id)} title="Excluir"><FiTrash2 /></button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="5" style={{textAlign: 'center', padding: '2rem', color: '#64748b'}}>Nenhum registro nesta aba.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {viewState === 'wizard' && (
                <Wizard 
                    colaboradores={funcionariosAtivos}
                    onClose={() => setViewState('list')}
                    onFinish={() => { fetchData(); setViewState('list'); }}
                />
            )}

            {viewState === 'details' && selectedItem && (
                <DetailModal 
                    data={selectedItem}
                    onClose={() => { setSelectedItem(null); setViewState('list'); }}
                    onUpdate={() => { handleDetail(selectedItem.id); fetchData(); }}
                />
            )}
        </div>
    );
};

// =========================================================================================
// 🔮 WIZARD DE CRIAÇÃO
// =========================================================================================
const Wizard = ({ colaboradores, onClose, onFinish }) => {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        user_id: '', tipo_rescisao: 'demissao_sem_justa_causa', data_saida: '',
        aviso_previo_tipo: 'trabalhado', motivo_detalhado: '', solicitar_substituicao: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedUser = colaboradores.find(c => c.id === parseInt(form.user_id));

    const handleSave = async () => {
        setIsSubmitting(true);
        try {
            await api.post('/api/rh/rescissoes', form);
            toast.success("Rescisão registrada com sucesso.");
            onFinish();
        } catch (e) {
            toast.error("Erro ao salvar.");
        } finally { setIsSubmitting(false); }
    };

    return (
        <div className="overlay-backdrop">
            <div className="wizard-container">
                <div className="wizard-sidebar">
                    <h3>Novo Desligamento</h3>
                    <ul className="steps-list">
                        <li className={step === 1 ? 'active' : (step > 1 ? 'done' : '')}>1. Seleção</li>
                        <li className={step === 2 ? 'active' : (step > 2 ? 'done' : '')}>2. Detalhes</li>
                        <li className={step === 3 ? 'active' : ''}>3. Confirmação</li>
                    </ul>
                </div>
                <div className="wizard-main">
                    {step === 1 && (
                        <div className="wizard-step fade-in">
                            <h4>Quem será desligado?</h4>
                            <div className="form-group">
                                <label>Colaborador</label>
                                <select className="input-modern" onChange={e => setForm({...form, user_id: e.target.value})} value={form.user_id}>
                                    <option value="">Selecione...</option>
                                    {colaboradores.map(c => <option key={c.id} value={c.id}>{c.nome} — {c.cargo}</option>)}
                                </select>
                            </div>
                            {selectedUser && (
                                <div className="user-card-preview">
                                    <FaUserTie size={32} />
                                    <div>
                                        <h5>{selectedUser.nome}</h5>
                                        <p>{selectedUser.area} • {selectedUser.cargo}</p>
                                        <span className="salary-tag">Salário: {formatCurrency(selectedUser.salario)}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 2 && (
                        <div className="wizard-step fade-in">
                            <h4>Dados da Rescisão</h4>
                            <div className="row-2">
                                <div className="form-group">
                                    <label>Tipo</label>
                                    <select className="input-modern" value={form.tipo_rescisao} onChange={e => setForm({...form, tipo_rescisao: e.target.value})}>
                                        <option value="demissao_sem_justa_causa">Demissão s/ Justa Causa</option>
                                        <option value="pedido_demissao">Pedido de Demissão</option>
                                        <option value="justa_causa">Justa Causa</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Data Saída</label>
                                    <input type="date" className="input-modern" value={form.data_saida} onChange={e => setForm({...form, data_saida: e.target.value})} />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Aviso Prévio</label>
                                <div className="radio-group">
                                    {['trabalhado', 'indenizado', 'dispensado'].map(type => (
                                        <label key={type} className={form.aviso_previo_tipo === type ? 'selected' : ''}>
                                            <input type="radio" name="aviso" value={type} checked={form.aviso_previo_tipo === type} onChange={() => setForm({...form, aviso_previo_tipo: type})} />
                                            {type.toUpperCase()}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="checkbox-wrapper">
                                    <input type="checkbox" checked={form.solicitar_substituicao} onChange={e => setForm({...form, solicitar_substituicao: e.target.checked})} />
                                    <span>Abrir vaga de substituição automaticamente?</span>
                                </label>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="wizard-step fade-in center-text">
                            <FiAlertTriangle className="icon-big warning" style={{color: '#f59e0b', fontSize: '3rem', display: 'block', margin: '0 auto 1rem'}} />
                            <h4 style={{textAlign: 'center'}}>Confirme os dados</h4>
                            <p style={{textAlign: 'center', marginBottom: '2rem'}}>Você está iniciando o processo para <strong>{selectedUser?.nome}</strong>.</p>
                            <div className="summary-box" style={{background: '#f8fafc', padding: '1.5rem', borderRadius: '8px'}}>
                                <p><strong>Motivo:</strong> {form.tipo_rescisao.replace(/_/g, ' ')}</p>
                                <p><strong>Data Saída:</strong> {formatDate(form.data_saida)}</p>
                            </div>
                        </div>
                    )}

                    <div className="wizard-actions">
                        <button className="btn-ghost" onClick={step === 1 ? onClose : () => setStep(step - 1)}>Voltar</button>
                        <button className="btn-primary" disabled={isSubmitting} onClick={() => {
                            if(step === 1 && !form.user_id) return toast.warning("Selecione um colaborador");
                            if(step === 2 && !form.data_saida) return toast.warning("Data é obrigatória");
                            step === 3 ? handleSave() : setStep(step + 1);
                        }}>
                            {step === 3 ? (isSubmitting ? 'Processando...' : 'Confirmar') : 'Próximo'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =========================================================================================
// 🧾 DETALHES + HOLERITE (CORRIGIDO E COMPACTO)
// =========================================================================================
const DetailModal = ({ data, onClose, onUpdate }) => {
    const [calculo, setCalculo] = useState(data.calculo || []);
    const [loadingAction, setLoadingAction] = useState(false);

    // Totais
    const totalProv = calculo.filter(i => i.tipo === 'provento').reduce((a,b) => a + parseFloat(b.val || b.valor), 0);
    const totalDesc = calculo.filter(i => i.tipo === 'desconto').reduce((a,b) => a + parseFloat(b.val || b.valor), 0);
    const liquido = totalProv - totalDesc;

    const handleSimulate = () => {
        const fgts = prompt("Saldo FGTS para multa (R$):", "0");
        if (fgts === null) return;
        setLoadingAction(true);
        setTimeout(() => {
            setCalculo(calcularVerbasSimuladas(data, parseFloat(fgts.replace(',','.'))));
            setLoadingAction(false);
            toast.info("Cálculo simulado gerado.");
        }, 600);
    };

    const handleFinish = async () => {
        if(!window.confirm("ATENÇÃO: Isso irá concluir a rescisão e inativar o usuário. Continuar?")) return;
        try {
            // AQUI: Chama apenas a rota de conclusão da rescisão
            await api.post(`/api/rh/rescissoes/${data.id}/concluir`);
            toast.success("Processo finalizado!");
            onUpdate();
        } catch (e) { toast.error("Erro ao concluir."); }
    };

    const handleUpload = async (e) => {
        if(!e.target.files[0]) return;
        const formData = new FormData();
        formData.append('documento_rescisao', e.target.files[0]);
        try {
            await api.post(`/api/rh/rescissoes/${data.id}/upload-documento`, formData);
            toast.success("Upload realizado.");
            onUpdate();
        } catch (e) { toast.error("Erro no upload."); }
    };

    return (
        <div className="overlay-backdrop">
            <div className="modal-fullscreen">
                <div className="modal-top-bar">
                    <div className="flex-center">
                        <button onClick={onClose} className="btn-icon"><FiArrowLeft /></button>
                        <h2>Rescisão #{data.id} - {data.nome_colaborador}</h2>
                        <StatusBadge status={data.status} />
                    </div>
                    <div className="flex-center gap-2 no-print">
                        <button className="btn-ghost" onClick={() => window.print()}><FiPrinter /> Imprimir</button>
                        {data.status !== 'concluida' && (
                            <button className="btn-success" onClick={handleFinish}>Concluir Processo</button>
                        )}
                    </div>
                </div>

                <div className="modal-layout">
                    {/* Sidebar com Timeline */}
                    <aside className="details-sidebar no-print">
                        <div className="timeline-container">
                            <h4>Progresso</h4>
                            <div className="timeline">
                                <div className={`timeline-item done`}>
                                    <div className="dot"></div>
                                    <div className="content">
                                        <strong>Início</strong>
                                        <p>Solicitado por {data.nome_admin_registro || 'Admin'}</p>
                                    </div>
                                </div>
                                <div className={`timeline-item ${calculo.length > 0 ? 'done' : 'active'}`}>
                                    <div className="dot"></div>
                                    <div className="content">
                                        <strong>Cálculo</strong>
                                        <p>{calculo.length > 0 ? 'Realizado' : 'Pendente'}</p>
                                    </div>
                                </div>
                                <div className={`timeline-item ${data.path_documento_assinado ? 'done' : (calculo.length > 0 ? 'active' : '')}`}>
                                    <div className="dot"></div>
                                    <div className="content">
                                        <strong>Assinatura</strong>
                                        <p>{data.path_documento_assinado ? 'TRCT Anexado' : 'Aguardando Upload'}</p>
                                    </div>
                                </div>
                                <div className={`timeline-item ${data.status === 'concluida' ? 'done' : ''}`}>
                                    <div className="dot"></div>
                                    <div className="content">
                                        <strong>Conclusão</strong>
                                        <p>Baixa e Pagamento</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="actions-panel">
                            <h4>Ações Rápidas</h4>
                            {data.status !== 'concluida' && (
                                <>
                                    <button className="btn-secondary full" onClick={handleSimulate} disabled={loadingAction}>
                                        <FaCalculator /> {loadingAction ? 'Calculando...' : 'Simular Verbas'}
                                    </button>
                                    <label className="btn-secondary full">
                                        <FiUpload /> Upload TRCT
                                        <input type="file" hidden onChange={handleUpload} />
                                    </label>
                                </>
                            )}
                            {data.path_documento_assinado && (
                                <button className="btn-primary full">
                                    <FiDownload /> Baixar TRCT Assinado
                                </button>
                            )}
                        </div>
                    </aside>

                    {/* Área Principal: Holerite */}
                    <main className="details-paper-area">
                        <div className="paper-document">
                            <div className="paper-header">
                                <div className="company-brand">TERMINO DE CONTRATO DE TRABALHO</div>
                                <div className="doc-meta">
                                    <span>Ref: {data.id}</span>
                                    <span>Data: {formatDate(new Date())}</span>
                                </div>
                            </div>

                            <div className="paper-employee-info">
                                <div>
                                    <label>Empregado</label>
                                    <span>{data.nome_colaborador}</span>
                                </div>
                                <div>
                                    <label>Admissão</label>
                                    <span>{formatDate(data.data_admissao)}</span>
                                </div>
                                <div>
                                    <label>Afastamento</label>
                                    <span>{formatDate(data.data_saida)}</span>
                                </div>
                                <div>
                                    <label>Causa</label>
                                    <span>{data.tipo_rescisao?.replace(/_/g, ' ')}</span>
                                </div>
                            </div>

                            {calculo.length === 0 ? (
                                <div className="empty-calc">
                                    <FaMoneyBillWave />
                                    <p>Nenhum cálculo realizado. Utilize o menu lateral.</p>
                                </div>
                            ) : (
                                <>
                                    <table className="paper-table">
                                        <thead>
                                            <tr>
                                                <th>Rubrica</th>
                                                <th className="right">Vencimentos</th>
                                                <th className="right">Descontos</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {calculo.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>{item.item_descricao || item.desc}</td>
                                                    <td className="right">
                                                        {item.tipo === 'provento' ? formatCurrency(item.val || item.valor) : ''}
                                                    </td>
                                                    <td className="right">
                                                        {item.tipo === 'desconto' ? formatCurrency(item.val || item.valor) : ''}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    <div className="paper-footer">
                                        <div className="total-group">
                                            <span>Total Vencimentos</span>
                                            <strong>{formatCurrency(totalProv)}</strong>
                                        </div>
                                        <div className="total-group">
                                            <span>Total Descontos</span>
                                            <strong>{formatCurrency(totalDesc)}</strong>
                                        </div>
                                        <div className="total-group net">
                                            <span>Líquido a Receber</span>
                                            <strong>{formatCurrency(liquido)}</strong>
                                        </div>
                                    </div>

                                    <div className="paper-signatures">
                                        <div className="sig-line">Empregador</div>
                                        <div className="sig-line">Empregado</div>
                                    </div>
                                </>
                            )}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
};

// --- Componente de Badge ---
const StatusBadge = ({ status }) => {
    const map = {
        'em_processamento': { class: 'bg-yellow', text: 'Em Andamento' },
        'concluida': { class: 'bg-green', text: 'Concluída' },
        'cancelada': { class: 'bg-red', text: 'Cancelada' }
    };
    const style = map[status] || { class: 'bg-gray', text: status };
    return <span className={`badge ${style.class}`}>{style.text}</span>;
};

export default RescisoesDemissoes;